import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Users,
  Building,
  Search,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  UserPlus,
  UserMinus,
  Check,
  X,
  Bell,
  Settings2
} from 'lucide-react';
import { getAccounts, getAllUsers, updateUserRole } from '../api';
import Switch from '@mui/material/Switch';

// Function to assign clients to users
const assignClientToUser = async (userId, clientIds) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/auth/users/${userId}/assign-clients`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ clientIds })
    });

    if (!response.ok) {
      throw new Error('Error al asignar clientes');
    }

    return await response.json();
  } catch (error) {
    console.error('Error assigning clients to user:', error);
    throw error;
  }
};

// Function to get clients assigned to a user
const getUserClients = async (userId) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/auth/users/${userId}/clients`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error('Error al obtener clientes asignados');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting user clients:', error);
    return [];
  }
};

// --- Funciones para notificaciones ---
// Obtener preferencias de notificaciones
const getUserNotifications = async (userId) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/auth/users/${userId}/notifications`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) throw new Error('Error al obtener preferencias de notificación');
    return await response.json();
  } catch (error) {
    console.error('Error getting notifications:', error);
    return null;
  }
};

// Guardar/actualizar preferencias de notificaciones
const updateUserNotifications = async (userId, notifications) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/auth/users/${userId}/notifications`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(notifications)
    });
    if (!response.ok) throw new Error('Error al guardar preferencias de notificación');
    return await response.json();
  } catch (error) {
    console.error('Error updating notifications:', error);
    throw error;
  }
};

// Eliminar preferencias de notificaciones
const deleteUserNotifications = async (userId) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/auth/users/${userId}/notifications`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) throw new Error('Error al eliminar preferencias de notificación');
    return await response.json();
  } catch (error) {
    console.error('Error deleting notifications:', error);
    throw error;
  }
};

// Tipos de notificaciones disponibles
const notificationTypes = [
  { key: 'end_trip', label: 'Notificar al finalizar viajes' },
  { key: 'onroad_trip', label: 'Notificar al poner en transito' },
  { key: 'evidence_pf', label: 'Notificar al subir evidencia' },
  // Puedes agregar más tipos aquí
];

const ClientAdministration = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedClients, setSelectedClients] = useState([]);
  const [userClients, setUserClients] = useState({});
  const [expandedUsers, setExpandedUsers] = useState({});
  const [saving, setSaving] = useState(false);
  const [expandedNotifications, setExpandedNotifications] = useState({});
  const [userNotificationPrefs, setUserNotificationPrefs] = useState({});

  // Fetch users and clients when component mounts
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [usersData, clientsData] = await Promise.all([
          getAllUsers(),
          getAccounts()
        ]);

        // Filter only users with 'client' role
        const clientUsers = usersData.filter(user => user.role === 'client');
        
        setUsers(clientUsers);
        setFilteredUsers(clientUsers);
        setClients(clientsData);

        // Create an object to track expanded state for each user
        const initialExpandedState = {};
        clientUsers.forEach(user => {
          initialExpandedState[user.id] = false;
        });
        setExpandedUsers(initialExpandedState);

        // Fetch client assignments for each user with client role
        const clientAssignments = {};
        for (const user of clientUsers) {
          const userClientsData = await getUserClients(user.id);
          clientAssignments[user.id] = userClientsData.map(client => client.id);
        }
        setUserClients(clientAssignments);
        
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Error al cargar datos');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter users when search value changes
  useEffect(() => {
    if (searchValue.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user =>
        user.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        user.email.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchValue, users]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  // Refresh user list
  const handleRefresh = async () => {
    setLoading(true);
    try {
      const usersData = await getAllUsers();
      const clientUsers = usersData.filter(user => user.role === 'client');
      setUsers(clientUsers);
      setFilteredUsers(clientUsers);

      // Refresh client assignments
      const clientAssignments = {};
      for (const user of clientUsers) {
        const userClientsData = await getUserClients(user.id);
        clientAssignments[user.id] = userClientsData.map(client => client.id);
      }
      setUserClients(clientAssignments);

      toast.success('Lista de usuarios actualizada');
    } catch (error) {
      console.error('Error refreshing user list:', error);
      toast.error('Error al actualizar la lista de usuarios');
    } finally {
      setLoading(false);
    }
  };

  // Toggle user expansion to show clients
  const toggleUserExpansion = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
    
    if (!expandedUsers[userId]) {
      setSelectedUser(userId);
    } else {
      setSelectedUser(null);
    }
  };

  // Handle client selection for a user
  const handleClientSelection = (clientId) => {
    if (!selectedUser) return;
    
    setSelectedClients(prev => {
      if (prev.includes(clientId)) {
        return prev.filter(id => id !== clientId);
      } else {
        return [...prev, clientId];
      }
    });
  };

  // Save client assignments for a user
  const handleSaveAssignments = async () => {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      await assignClientToUser(selectedUser, selectedClients);
      
      // Update local state
      setUserClients(prev => ({
        ...prev,
        [selectedUser]: [...selectedClients]
      }));
      
      toast.success('Clientes asignados correctamente');
    } catch (error) {
      console.error('Error saving client assignments:', error);
      toast.error('Error al asignar clientes');
    } finally {
      setSaving(false);
    }
  };

  // Toggle notificaciones expandibles
  const toggleNotificationExpansion = (userId) => {
    setExpandedNotifications(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Al expandir notificaciones, cargar preferencias desde backend
  const handleExpandNotifications = async (userId) => {
    toggleNotificationExpansion(userId);
    if (!expandedNotifications[userId]) {
      // Solo cargar si se va a expandir
      try {
        const prefs = await getUserNotifications(userId);
        setUserNotificationPrefs(prev => ({
          ...prev,
          [userId]: prefs || {}
        }));
      } catch {
        toast.error('No se pudieron cargar las preferencias de notificación');
      }
    }
  };

  // Guardar automáticamente al cambiar un switch
  const handleToggleNotificationType = async (userId, typeKey) => {
    const prevPrefs = userNotificationPrefs[userId] || {};
    const newPrefs = {
      ...prevPrefs,
      [typeKey]: !prevPrefs[typeKey]
    };
    setUserNotificationPrefs(prev => ({
      ...prev,
      [userId]: newPrefs
    }));
    try {
      await updateUserNotifications(userId, newPrefs);
      toast.success('Preferencias de notificación actualizadas');
    } catch {
      toast.error('Error al guardar preferencias de notificación');
    }
  };

  // When a user is selected, load their assigned clients
  useEffect(() => {
    if (selectedUser) {
      setSelectedClients(userClients[selectedUser] || []);
    }
  }, [selectedUser, userClients]);

  return (
    <Card className="shadow-lg hover:shadow-xl transition-all duration-300 mt-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-medium">Administración de Clientes</CardTitle>
        <div className="flex space-x-2">
          <div className="relative">
            <input
              type="text"
              value={searchValue}
              onChange={handleSearchChange}
              placeholder="Buscar usuarios..."
              className="pl-10 pr-4 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-500 outline-none transition-colors duration-200"
            />
            <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
          </div>
          <Button 
            onClick={handleRefresh}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800"
            title="Refrescar lista"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center items-center p-4">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {filteredUsers.length === 0 ? (
              <p className="text-center text-gray-500 py-10">
                No se encontraron usuarios con rol de cliente
              </p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clientes Asignados</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <React.Fragment key={user.id}>
                      <tr 
                        className={`hover:bg-gray-50 cursor-pointer ${selectedUser === user.id ? 'bg-blue-50' : ''}`}
                        onClick={() => toggleUserExpansion(user.id)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {expandedUsers[user.id] ? (
                              <ChevronDown className="h-4 w-4 mr-2 text-gray-500" />
                            ) : (
                              <ChevronRight className="h-4 w-4 mr-2 text-gray-500" />
                            )}
                            <span className="font-medium text-gray-700">{user.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-500">{user.email}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-500">
                            {userClients[user.id]?.length || 0} clientes
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex items-center gap-2 justify-end">
                          <button
                            className="text-blue-600 hover:text-blue-900 mr-3 p-2 rounded-full hover:bg-blue-100 focus:outline-none"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleUserExpansion(user.id);
                            }}
                            title="Administrar clientes"
                            aria-label="Administrar clientes"
                          >
                            <Settings2 className="h-5 w-5" />
                          </button>
                          {/* Botón para notificaciones */}
                          <button
                            className="text-yellow-600 hover:text-yellow-900 p-2 rounded-full hover:bg-yellow-100 focus:outline-none"
                            onClick={e => {
                              e.stopPropagation();
                              handleExpandNotifications(user.id);
                            }}
                            title="Configurar notificaciones"
                            aria-label="Configurar notificaciones"
                          >
                            <Bell className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                      {expandedUsers[user.id] && (
                        <tr>
                          <td colSpan="4" className="px-6 py-4 bg-gray-50">
                            <div className="border rounded-md p-4">
                              <h4 className="font-medium text-gray-800 mb-3">
                                Asignación de clientes para: {user.name}
                              </h4>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
                                {clients.map((client) => (
                                  <div 
                                    key={client.id} 
                                    className={`border rounded-md p-2 flex items-center justify-between cursor-pointer ${
                                      selectedClients.includes(client.id) ? 'border-blue-500 bg-blue-50' : 'hover:border-gray-400'
                                    }`}
                                    onClick={() => handleClientSelection(client.id)}
                                  >
                                    <div className="flex items-center">
                                      <Building className="h-4 w-4 mr-2 text-gray-600" />
                                      <span className="text-sm">{client.nombre_cuenta}</span>
                                    </div>
                                    {selectedClients.includes(client.id) ? (
                                      <Check className="h-4 w-4 text-blue-600" />
                                    ) : (
                                      <div className="h-4 w-4 rounded-full border border-gray-300"></div>
                                    )}
                                  </div>
                                ))}
                              </div>
                              <div className="flex justify-end space-x-2 mt-4">
                                <Button
                                  onClick={() => {
                                    setSelectedClients([]);
                                    toggleUserExpansion(user.id);
                                  }}
                                  className="bg-gray-200 hover:bg-gray-300 text-gray-800"
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  onClick={handleSaveAssignments}
                                  className="bg-blue-600 hover:bg-blue-700 text-white"
                                  disabled={saving}
                                >
                                  {saving ? 'Guardando...' : 'Guardar cambios'}
                                </Button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      {/* Sección de notificaciones expandible */}
                      {expandedNotifications[user.id] && (
                        <tr>
                          <td colSpan="4" className="px-6 py-4 bg-gray-50">
                            <div className="border rounded-md p-4">
                              <h4 className="font-medium text-gray-800 mb-3">
                                Notificaciones para: {user.name}
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {notificationTypes.map(type => (
                                  <div key={type.key} className="flex items-center justify-between p-2 bg-white rounded shadow-sm">
                                    <span className="text-sm">{type.label}</span>
                                    <div className="flex items-center">
                                      <Switch
                                        checked={userNotificationPrefs[user.id]?.[type.key] || false}
                                        onChange={() => handleToggleNotificationType(user.id, type.key)}
                                        color="primary"
                                        inputProps={{ 'aria-label': type.label }}
                                      />
                                      <span className="ml-2">
                                        {userNotificationPrefs[user.id]?.[type.key] ? 'Sí' : 'No'}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </CardContent>
      <CardFooter className="bg-gray-50 border-t border-gray-100 text-xs text-gray-600 py-3">
        <p>Solo se muestran usuarios con rol de cliente</p>
      </CardFooter>
    </Card>
  );
};

export default ClientAdministration;